<template>
  <div class="col-12 col-xxl-6 bc-col">
    <div class="bc-card">
      <BudgetCardHeader :summary="summary" @update:scope="onScopeChange" @update:headType="onHeadTypeChange"/>
      <div v-if="! currentScope" class="bcc-empty">No approved budget</div>
      <template v-else>
        <div class="bcc-controls">
          <div v-if="views.length > 1" class="bcc-toggle" role="group">
            <button
              v-for="view in views"
              :key="view.key"
              type="button"
              class="bcc-toggle-btn"
              :class="{ 'bcc-toggle-active': view.key === activeView.key }"
              @click="onSelectView(view.key)"
            >{{ view.name }}</button>
          </div>
          <div v-if="! emptyText" class="bcc-legend">
            <span v-for="item in legendItems" :key="item.name" class="bcc-legend-item">
              <span class="bcc-legend-swatch" :style="{ backgroundColor: item.color, borderColor: item.border }"></span>
              {{ item.name }}
            </span>
          </div>
        </div>
        <div v-if="emptyText" class="bcc-empty">{{ emptyText }}</div>
        <BudgetChart
          v-else
          :key="activeView.key"
          :rows="activeRows"
          :direction="activeView.direction"
          :favourableWhen="summary.favourable_when"
          :budgetMark="summary.budget_mark"
          :showUsed="summary.show_used"
          :budgetOnly="isItemHead"
          :asOf="summary.as_of"
          :chartId="`${itemKey}_${activeView.key}`"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import BudgetCardHeader from '@/components/molecule/company/dashboard/dynamic-dashboard/budget/BudgetCardHeader'
import BudgetChart from '@/components/molecule/company/dashboard/dynamic-dashboard/budget/BudgetChart'
import { ACTUAL_COLOR, BUDGET_BORDER_COLOR, BUDGET_COLOR, BUDGET_LINE_COLOR, ITEM_HEAD, LINE_MARK, headDataOf, periodBounds } from '@/components/molecule/company/dashboard/dynamic-dashboard/budget/budgetFigures'

const GROUP_VIEW = 'group'
const PERIOD_VIEW = 'period'

const props = defineProps({
  statisticsItem: Object,
  itemKey: String,
  page: Object
})

const summary = computed(() => props.statisticsItem[props.itemKey].data || {})
const views = computed(() => summary.value.views || [])

const currentScope = ref(null)
const selectedView = ref(null)
const headType = ref(null)

const onScopeChange = (scope) => {
  currentScope.value = scope
}

const onHeadTypeChange = (value) => {
  headType.value = value
}

const onSelectView = (key) => {
  selectedView.value = key
}

const activeView = computed(() => views.value.find(view => view.key === selectedView.value) || views.value[0] || {})

const isItemHead = computed(() => headType.value === ITEM_HEAD)

const scopeRows = computed(() => {
  const head = headDataOf(currentScope.value, headType.value)
  if (! head) return {}
  return Array.isArray(head) ? { [PERIOD_VIEW]: head } : head.rows || {}
})

const rowsOf = (key) => scopeRows.value[key] || []

const activeRows = computed(() => rowsOf(activeView.value.key))

const legendItems = computed(() => {
  const isLine = summary.value.budget_mark === LINE_MARK
  const budgetItem = { name: 'Budget', color: isLine ? BUDGET_LINE_COLOR : BUDGET_COLOR, border: isLine ? BUDGET_LINE_COLOR : BUDGET_BORDER_COLOR }
  if (isItemHead.value) return [budgetItem]
  return [budgetItem, { name: 'Actual', color: ACTUAL_COLOR, border: ACTUAL_COLOR }]
})

const isPeriodReached = (row) => {
  if (! isItemHead.value) return row.actual !== null
  return periodBounds(row.label).first <= summary.value.as_of.slice(0, 7)
}

const hasStarted = computed(() => {
  const periodRows = rowsOf(PERIOD_VIEW)
  return ! periodRows.length || periodRows.some(isPeriodReached)
})
const hasValues = computed(() => activeRows.value.some(row => row.budget || row.actual))

const emptyText = computed(() => {
  if (activeView.value.key === GROUP_VIEW && ! hasStarted.value) return 'No period has started yet'
  return hasValues.value ? '' : 'No budget or actual yet'
})
</script>

<style scoped src="./budget/budgetCard.css"></style>
